
import { useState, useEffect } from 'react';
import type { Application } from '@/types/database.types';
import { useAutosave } from './application/use-autosave';
import { useManualSave } from './application/use-manual-save';

/**
 * Hook to handle all application saving functionality
 */
export const useApplicationSave = (
  application: Application | null,
  setApplication: (app: Application | null) => void,
  calculateProgress: () => number
) => {
  // Track saving state and last saved time
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  
  // Get autosave functionality
  const { 
    lastSaved: autoSaveLastSaved, 
    debouncedSave,
    saveApplicationData,
    isSaving: isAutoSaving 
  } = useAutosave(application, setApplication);
  
  // Sync the last saved time from autosave
  useEffect(() => {
    if (autoSaveLastSaved) {
      setLastSaved(autoSaveLastSaved);
    }
  }, [autoSaveLastSaved]);
  
  // Sync the saving state from autosave
  useEffect(() => {
    setIsSaving(isAutoSaving);
  }, [isAutoSaving]);
  
  // Get manual save functionality
  const { saveProgress } = useManualSave(
    application,
    setApplication,
    calculateProgress,
    lastSaved,
    setLastSaved,
    isSaving,
    setIsSaving
  );

  return {
    lastSaved,
    saveProgress,
    debouncedSave,
    saveApplicationData
  };
};
